import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { pushNotificationService } from "../services/pushNotificationService";

/**
 * Type for subscription state returned by getSubscriptionState()
 */
interface SubscriptionState {
  isSubscribed: boolean;
  needsResubscription: boolean;
  browserSubscription: PushSubscription | null;
}

/**
 * Hook for managing push notification subscriptions
 */
export function usePushNotifications() {
  const queryClient = useQueryClient();
  const [permissionState, setPermissionState] =
    useState<NotificationPermission>(
      pushNotificationService.getPermissionState()
    );

  // Check if push is supported
  const isSupported = pushNotificationService.isSupported();

  // Query for current subscription state (reconciled with backend)
  const {
    data: subscriptionState,
    isLoading: isCheckingSubscription,
    refetch: refetchSubscriptionStatus,
  } = useQuery<SubscriptionState>({
    queryKey: ["pushSubscription", "state"],
    queryFn: () => pushNotificationService.getSubscriptionState(),
    enabled: isSupported && permissionState !== "denied",
    staleTime: 30000, // Cache for 30 seconds to avoid excessive API calls
  });

  // Derived state
  const isSubscribed = subscriptionState?.isSubscribed ?? false;
  const needsResubscription = subscriptionState?.needsResubscription ?? false;

  // Query for user's subscriptions list
  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useQuery({
    queryKey: ["pushSubscriptions", "list"],
    queryFn: async () => {
      return pushNotificationService.getSubscriptions();
    },
    enabled: isSupported && permissionState === "granted",
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: (deviceName?: string) =>
      pushNotificationService.subscribe(deviceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pushSubscription"] });
      queryClient.invalidateQueries({ queryKey: ["pushSubscriptions"] });
    },
  });

  // Unsubscribe mutation
  const unsubscribeMutation = useMutation({
    mutationFn: () => pushNotificationService.unsubscribe(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pushSubscription"] });
      queryClient.invalidateQueries({ queryKey: ["pushSubscriptions"] });
    },
  });

  // Request permission
  const requestPermission = useCallback(async () => {
    const permission = await pushNotificationService.requestPermission();
    setPermissionState(permission);
    return permission;
  }, []);

  // Subscribe to push
  const subscribe = useCallback(
    async (deviceName?: string) => {
      return subscribeMutation.mutateAsync(deviceName);
    },
    [subscribeMutation]
  );

  // Unsubscribe from push
  const unsubscribe = useCallback(async () => {
    return unsubscribeMutation.mutateAsync();
  }, [unsubscribeMutation]);

  // Listen for permission changes
  useEffect(() => {
    if (!isSupported) return;

    // Check permission on mount
    setPermissionState(Notification.permission);

    // Poll for permission changes (no native event for this)
    const interval = setInterval(() => {
      const currentPermission = Notification.permission;
      if (currentPermission !== permissionState) {
        setPermissionState(currentPermission);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isSupported, permissionState]);

  // Auto-restore subscription if browser state was lost but user had push enabled
  useEffect(() => {
    if (needsResubscription && permissionState === "granted" && !subscribeMutation.isPending) {
      console.log("[usePushNotifications] Auto-restoring subscription after PWA update");
      subscribeMutation.mutate(navigator.platform || "Unknown Device");
    }
  }, [needsResubscription, permissionState, subscribeMutation]);

  return {
    // State
    isSupported,
    permissionState,
    isSubscribed,
    needsResubscription,
    subscriptions: subscriptions ?? [],

    // Loading states
    isCheckingSubscription,
    isLoadingSubscriptions,
    isSubscribing: subscribeMutation.isPending,
    isUnsubscribing: unsubscribeMutation.isPending,

    // Actions
    requestPermission,
    subscribe,
    unsubscribe,
    refetchSubscriptionStatus,

    // Errors
    subscribeError: subscribeMutation.error,
    unsubscribeError: unsubscribeMutation.error,
  };
}
